import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, unref as _unref } from "vue"

const _hoisted_1 = {
  "data-test-card": "",
  class: "flex flex-col gap-3 rounded-[10px] border border-gray-200 bg-white px-[22px] py-5 shadow-[0_1px_2px_rgba(15,23,34,0.04),0_2px_8px_-4px_rgba(15,23,34,0.06)]"
}
const _hoisted_2 = { class: "flex items-start justify-between gap-3" }
const _hoisted_3 = { class: "min-w-0" }
const _hoisted_4 = { class: "flex items-center gap-2" }
const _hoisted_5 = {
  "data-card-title": "",
  class: "m-0 text-base font-semibold text-gray-900"
}
const _hoisted_6 = {
  "data-status": "",
  class: "m-0 mt-1 text-[13px] text-gray-600"
}
const _hoisted_7 = { class: "flex flex-wrap items-center gap-2" }
const _hoisted_8 = {
  key: 0,
  "data-ends-other": "",
  class: "m-0 text-[13px] text-gray-500"
}
const _hoisted_9 = {
  key: 1,
  "data-reason": "",
  class: "m-0 rounded-md bg-amber-50 px-3 py-2 text-[13px] text-amber-800"
}
const _hoisted_10 = {
  key: 2,
  "data-start-error": "",
  role: "alert",
  class: "m-0 text-[13px] text-red-700"
}
const _hoisted_11 = {
  key: 3,
  "data-transcript": "",
  class: "m-0 flex list-none flex-col gap-2 rounded-lg bg-[#f9fafb] p-3"
}
const _hoisted_12 = ["data-direction"]

import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import WizardButton from '@/components/neue/wizard/WizardButton.vue'
import { useAgentTestConversation } from '@/composables/useAgentTestConversation'
import { agentTestTitle, isStructuralReason, reasonCopy, statusLine } from '@/helpers/agentTestCopy'

const WINBACK = 'WinbackAgent'
const NO_NUMBER = 'no_test_contact'


export default {
  __name: 'AgentTestConversationCard',
  props: {
  // JSONAPI agent row: attributes.type, attributes.test_mode
  agent: { type: Object, required: true },
  // The agent's playbook row; Winback reads raw_context.slipping.enabled from it.
  playbook: { type: Object, default: null },
  storeName: { type: String, default: '' },
  storefrontUrl: { type: String, default: '' },
  hasNumber: { type: Boolean, default: false },
  // Set only when another agent's test is the live one.
  liveTestAgentName: { type: String, default: '' }
},
  emits: ['go-live', 'live-test'],
  setup(__props, { emit: __emit }) {

const props = __props
const emit = __emit

const { state, refresh, start } = useAgentTestConversation(props.agent.id)
refresh()

watch(
  () => props.hasNumber,
  (now, before) => {
    if (now && !before) refresh()
  }
)
watch(
  () => state.liveTestAgentId,
  id => emit('live-test', id)
)

const type = computed(() => props.agent.attributes?.type)
const testing = computed(() => Boolean(props.agent.attributes?.test_mode))
const title = computed(() => agentTestTitle(type.value, props.storeName))
const pill = computed(() => (testing.value ? 'Testing' : 'Active'))
const pillClass = computed(() => (testing.value ? 'bg-indigo-100 text-indigo-700' : 'bg-green-100 text-green-700'))
const status = computed(() => statusLine(state.session, props.liveTestAgentName))
const messages = computed(() => state.session?.messages ?? [])

const slippingEnabled = computed(() => Boolean(props.playbook?.attributes?.raw_context?.slipping?.enabled))
const sends = computed(() => {
  if (type.value !== WINBACK) return [{ label: state.session ? 'Send again' : 'Text me', segment: undefined }]
  const lost = { label: 'Text me the lost message', segment: 'lost' }
  return slippingEnabled.value ? [lost, { label: 'Text me the slipping message', segment: 'slipping' }] : [lost]
})

const sending = ref('')
const structural = computed(() => isStructuralReason(state.reasonCode))
const blocked = computed(() => !props.hasNumber || structural.value || sending.value !== '')

const note = computed(() => {
  if (!props.hasNumber) return reasonCopy(NO_NUMBER)
  if (state.eligible && !structural.value) return ''
  return reasonCopy(state.reasonCode, {
    agentType: type.value,
    storefrontUrl: props.storefrontUrl,
    reason: state.reason
  })
})

// A structural reason (no cart yet, no order yet...) clears once the merchant acts on their phone,
// so it is checked again when they come back to this tab or press Check again.
const checkAgain = () => {
  if (structural.value && !state.loading) refresh()
}
const onVisible = () => {
  if (document.visibilityState === 'visible') checkAgain()
}
onMounted(() => {
  document.addEventListener('visibilitychange', onVisible)
  window.addEventListener('focus', checkAgain)
})
onBeforeUnmount(() => {
  document.removeEventListener('visibilitychange', onVisible)
  window.removeEventListener('focus', checkAgain)
})

const text = async send => {
  if (blocked.value) return
  sending.value = send.label
  try {
    await start(send.segment)
  } finally {
    sending.value = ''
  }
}

return (_ctx, _cache) => {
  return (_openBlock(), _createElementBlock("article", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("h4", _hoisted_5, _toDisplayString(title.value), 1),
          _createElementVNode("span", {
            "data-pill": "",
            class: _normalizeClass(["rounded-full px-2 py-0.5 text-xs font-semibold", pillClass.value])
          }, _toDisplayString(pill.value), 3)
        ]),
        _createElementVNode("p", _hoisted_6, _toDisplayString(status.value), 1)
      ]),
      (testing.value)
        ? (_openBlock(), _createBlock(WizardButton, {
            key: 0,
            onClick: _cache[0] || (_cache[0] = $event => (emit('go-live', __props.agent.id)))
          }, {
            default: _withCtx(() => [...(_cache[1] || (_cache[1] = [
              _createTextVNode("Go live", -1)
            ]))]),
            _: 1
          }))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_7, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sends.value, (send) => {
        return (_openBlock(), _createBlock(WizardButton, {
          key: send.label,
          variant: "primary",
          disabled: blocked.value,
          loading: sending.value === send.label,
          onClick: $event => (text(send))
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(send.label), 1)
          ]),
          _: 2
        }, 1032, ["disabled", "loading", "onClick"]))
      }), 128)),
      (structural.value)
        ? (_openBlock(), _createBlock(WizardButton, {
            key: 0,
            "data-check-again": "",
            loading: _unref(state).loading,
            onClick: checkAgain
          }, {
            default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
              _createTextVNode(" Check again ", -1)
            ]))]),
            _: 1
          }, 8, ["loading"]))
        : _createCommentVNode("", true)
    ]),
    (__props.liveTestAgentName)
      ? (_openBlock(), _createElementBlock("p", _hoisted_8, " Starting this test ends your " + _toDisplayString(__props.liveTestAgentName) + " test. ", 1))
      : _createCommentVNode("", true),
    (note.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_9, _toDisplayString(note.value), 1))
      : _createCommentVNode("", true),
    (_unref(state).error)
      ? (_openBlock(), _createElementBlock("p", _hoisted_10, _toDisplayString(_unref(state).error), 1))
      : _createCommentVNode("", true),
    (messages.value.length)
      ? (_openBlock(), _createElementBlock("ol", _hoisted_11, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(messages.value, (message) => {
            return (_openBlock(), _createElementBlock("li", {
              key: message.id,
              "data-message": "",
              "data-direction": message.direction,
              class: _normalizeClass(["max-w-[80%] whitespace-pre-line rounded-2xl px-3 py-2 text-sm", 
          message.direction === 'customer'
            ? 'self-end bg-gray-800 text-white'
            : 'self-start bg-white text-gray-900 shadow-sm'
        ])
            }, _toDisplayString(message.body), 11, _hoisted_12))
          }), 128))
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

}